import { ReactNode, FC, createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
  badgeStyle,
  cardOverlayStyle,
  cardOverlayVisibleStyle,
  radioCardRadioStyle,
  wrapperStyle,
} from './RadioCard.css';
import { getClassNames } from '../../../util/css';
import { Image as SparkyImage } from '../../Image/Image';
import Ribbon from '../../Ribbon/Ribbon';
import { RadioBase } from '../RadioBase';
import { useRadioGroup } from '../RadioGroupContext';
import { RadioIndicator } from '../RadioIndicator';

const cardOverlayClassname = 'sparky-cardOverlay';

type RadioCardContextValue = {
  value: string;
  labelId?: string;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
};

const RadioCardContext = createContext<RadioCardContextValue | undefined>(undefined);

const useRadioCard = () => {
  const context = useContext(RadioCardContext);

  if (context === undefined) {
    throw new Error(
      `useRadioCard must be used within a RadioCardContext.Provider. Make sure to place the Indicator component inside a RadioCard.`,
    );
  }

  return context;
};

const getCardLabel = (labelId: string | undefined, ariaLabelledby: string | undefined) => {
  if (labelId) {
    return document.getElementById(`${labelId}`)?.textContent ?? '';
  }

  if (ariaLabelledby) {
    return document.getElementById(`${ariaLabelledby.split(' ')[0]}`)?.textContent ?? '';
  }

  return '';
};

type ImageProps = {
  alt: string;
  src: string;
};

const Image: FC<ImageProps> = ({ ...props }) => (
  <SparkyImage {...props} aspectRatio="16/9" objectFit="cover" width="100%"></SparkyImage>
);

const Indicator: FC = () => {
  const [dataLabel, setDataLabel] = useState('');
  const { value, labelId, ariaLabelledby, ariaDescribedby } = useRadioCard();

  useEffect(() => {
    setDataLabel(getCardLabel(labelId, ariaLabelledby));
  }, [ariaLabelledby, labelId, setDataLabel]);

  return (
    <RadioBase
      className={getClassNames('radiocard-button', radioCardRadioStyle)}
      aria-describedby={ariaDescribedby}
      aria-labelledby={ariaLabelledby}
      data-label={dataLabel}
      id={labelId}
      value={value}>
      <RadioIndicator />
    </RadioBase>
  );
};

type RadioCardLabelProps =
  | {
      labelId: string;
      ariaLabelledby?: never;
    }
  | {
      labelId?: never;
      ariaLabelledby: string;
    };

type RadioCardProps = {
  children: ReactNode;
  value: string;
  /**
   * Optional highlight label (typically a `Badge`) straddling the card's top border, outside the
   * padded content so it never competes with the card's own layout for width.
   *
   * Decorative by design: it is not part of the radio's accessible name and does not receive clicks,
   * so include anything it must announce in `ariaLabelledby` or the card's own content.
   */
  badge?: ReactNode;
  overflow?: 'hidden' | 'visible';
  className?: never;
} & RadioCardLabelProps;

type RadioCardCompoundCardProps = {
  Indicator: typeof Indicator;
  Ribbon: typeof Ribbon;
  Image: typeof Image;
};

export const RadioCard: FC<RadioCardProps> & RadioCardCompoundCardProps = ({
  children,
  value,
  badge,
  overflow = 'hidden',
  labelId,
  ariaLabelledby,
  className = '',
}) => {
  const { ariaDescribedby } = useRadioGroup();

  const contextValue = useMemo(
    () => ({ value, labelId, ariaLabelledby, ariaDescribedby }),
    [value, labelId, ariaLabelledby, ariaDescribedby],
  );

  return (
    <RadioCardContext.Provider value={contextValue}>
      <div className={wrapperStyle}>
        {badge ? <div className={badgeStyle}>{badge}</div> : null}
        <div
          className={getClassNames(
            cardOverlayClassname,
            cardOverlayStyle,
            overflow === 'visible' ? cardOverlayVisibleStyle : '',
            className,
          )}>
          {children}
        </div>
      </div>
    </RadioCardContext.Provider>
  );
};

RadioCard.Indicator = Indicator;
RadioCard.Indicator.displayName = 'RadioCard.Indicator';

RadioCard.Ribbon = Ribbon;
RadioCard.Ribbon.displayName = 'RadioCard.Ribbon';

RadioCard.Image = Image;
RadioCard.Image.displayName = 'RadioCard.Image';
